import { useState, useEffect, useCallback } from 'react';
import { Gamepad2, Trophy, RotateCcw, ChevronLeft, Pause, Play } from 'lucide-react';

interface Position {
  x: number;
  y: number;
}

export default function SnakeGame() {
  const [snake, setSnake] = useState<Position[]>([{ x: 10, y: 10 }]);
  const [food, setFood] = useState<Position>({ x: 15, y: 15 });
  const [direction, setDirection] = useState<Position>({ x: 0, y: 0 });
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(() => {
    return parseInt(localStorage.getItem('snakeHighScore') || '0');
  });
  const [isPaused, setIsPaused] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);

  const GRID_SIZE = 20;
  const CELL_SIZE = 14;

  const generateFood = useCallback((): Position => {
    let newFood: Position;
    do {
      newFood = {
        x: Math.floor(Math.random() * GRID_SIZE),
        y: Math.floor(Math.random() * GRID_SIZE)
      };
    } while (snake.some(segment => segment.x === newFood.x && segment.y === newFood.y));
    return newFood;
  }, [snake]);

  const resetGame = () => {
    setSnake([{ x: 10, y: 10 }]);
    setFood({ x: 15, y: 15 });
    setDirection({ x: 0, y: 0 });
    setGameOver(false);
    setScore(0);
    setIsPaused(false);
    setGameStarted(false);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!gameStarted) {
        setGameStarted(true);
      }
      
      switch(e.key) {
        case 'ArrowUp':
          if (direction.y === 0) setDirection({ x: 0, y: -1 });
          break;
        case 'ArrowDown':
          if (direction.y === 0) setDirection({ x: 0, y: 1 });
          break;
        case 'ArrowLeft':
          if (direction.x === 0) setDirection({ x: -1, y: 0 });
          break;
        case 'ArrowRight':
          if (direction.x === 0) setDirection({ x: 1, y: 0 });
          break;
        case ' ':
          e.preventDefault();
          setIsPaused(prev => !prev);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [direction, gameStarted]);

  useEffect(() => {
    if (gameOver || isPaused || !gameStarted) return;

    const gameInterval = setInterval(() => {
      setSnake(currentSnake => {
        const newSnake = [...currentSnake];
        const head = { ...newSnake[0] };
        head.x += direction.x;
        head.y += direction.y;

        // Check wall collision
        if (head.x < 0 || head.x >= GRID_SIZE || head.y < 0 || head.y >= GRID_SIZE) {
          setGameOver(true);
          return currentSnake;
        }

        // Check self collision
        if (newSnake.some(segment => segment.x === head.x && segment.y === head.y)) {
          setGameOver(true);
          return currentSnake;
        }

        newSnake.unshift(head);

        // Check food collision
        if (head.x === food.x && head.y === food.y) {
          setScore(prev => {
            const newScore = prev + 10;
            if (newScore > highScore) {
              setHighScore(newScore);
              localStorage.setItem('snakeHighScore', newScore.toString());
            }
            return newScore;
          });
          setFood(generateFood());
        } else {
          newSnake.pop();
        }

        return newSnake;
      });
    }, 150);

    return () => clearInterval(gameInterval);
  }, [direction, food, gameOver, isPaused, gameStarted, generateFood, highScore]);

  const handleTouch = (dir: string) => {
    if (!gameStarted) setGameStarted(true);
    
    switch(dir) {
      case 'up':
        if (direction.y === 0) setDirection({ x: 0, y: -1 });
        break;
      case 'down':
        if (direction.y === 0) setDirection({ x: 0, y: 1 });
        break;
      case 'left':
        if (direction.x === 0) setDirection({ x: -1, y: 0 });
        break;
      case 'right':
        if (direction.x === 0) setDirection({ x: 1, y: 0 });
        break;
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="px-4 pt-4">
        {/* 标题栏 */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-telegram-text flex items-center">
            <Gamepad2 className="w-6 h-6 mr-2 text-green-400" />
            贪吃蛇
          </h2>
          <div className="flex items-center space-x-3">
            <div className="text-right">
              <div className="text-xs text-telegram-secondary">分数</div>
              <div className="text-lg font-bold text-green-400">{score}</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-telegram-secondary flex items-center">
                <Trophy className="w-3 h-3 mr-1" />
                最高
              </div>
              <div className="text-sm font-bold text-yellow-400">{highScore}</div>
            </div>
          </div>
        </div>

        {/* 游戏区域 */}
        <div className="relative bg-telegram-dark rounded-xl overflow-hidden border-2 border-green-500/30 mb-4" style={{ width: GRID_SIZE * CELL_SIZE, height: GRID_SIZE * CELL_SIZE, margin: '0 auto' }}>
          {!gameStarted && !gameOver && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/60 z-10">
              <div className="text-center">
                <p className="text-white font-bold text-lg mb-2">点击方向键开始</p>
                <p className="text-white/60 text-sm">使用方向键或 WASD 控制</p>
              </div>
            </div>
          )}
          
          {gameOver && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/70 z-20">
              <div className="text-center p-4">
                <p className="text-red-400 font-bold text-2xl mb-2">游戏结束!</p>
                <p className="text-white mb-4">最终得分: {score}</p>
                <button
                  onClick={resetGame}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg font-medium flex items-center justify-center"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  重新开始
                </button>
              </div>
            </div>
          )}

          {isPaused && !gameOver && gameStarted && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
              <Pause className="w-12 h-12 text-white" />
            </div>
          )}

          {/* 渲染蛇 */}
          {snake.map((segment, index) => (
            <div
              key={index}
              className={`absolute rounded-sm ${index === 0 ? 'bg-green-400' : 'bg-green-500/80'}`}
              style={{
                left: segment.x * CELL_SIZE + 1,
                top: segment.y * CELL_SIZE + 1,
                width: CELL_SIZE - 2,
                height: CELL_SIZE - 2
              }}
            />
          ))}

          {/* 渲染食物 */}
          <div
            className="absolute bg-red-400 rounded-full animate-pulse"
            style={{
              left: food.x * CELL_SIZE + 2,
              top: food.y * CELL_SIZE + 2,
              width: CELL_SIZE - 4,
              height: CELL_SIZE - 4
            }}
          />
        </div>

        {/* 移动端控制按钮 */}
        <div className="flex flex-col items-center space-y-2 mb-4">
          <button
            onClick={() => handleTouch('up')}
            className="w-14 h-14 bg-telegram-dark rounded-full flex items-center justify-center active:bg-green-500/30 transition-colors border border-green-500/30"
          >
            <ChevronLeft className="w-8 h-8 text-white rotate-90" />
          </button>
          <div className="flex space-x-4">
            <button
              onClick={() => handleTouch('left')}
              className="w-14 h-14 bg-telegram-dark rounded-full flex items-center justify-center active:bg-green-500/30 transition-colors border border-green-500/30"
            >
              <ChevronLeft className="w-8 h-8 text-white" />
            </button>
            <button
              onClick={() => setIsPaused(!isPaused)}
              className="w-14 h-14 bg-telegram-dark rounded-full flex items-center justify-center active:bg-yellow-500/30 transition-colors border border-yellow-500/30"
            >
              {isPaused ? <Play className="w-6 h-6 text-yellow-400" /> : <Pause className="w-6 h-6 text-yellow-400" />}
            </button>
            <button
              onClick={() => handleTouch('right')}
              className="w-14 h-14 bg-telegram-dark rounded-full flex items-center justify-center active:bg-green-500/30 transition-colors border border-green-500/30"
            >
              <ChevronLeft className="w-8 h-8 text-white rotate-180" />
            </button>
          </div>
          <button
            onClick={() => handleTouch('down')}
            className="w-14 h-14 bg-telegram-dark rounded-full flex items-center justify-center active:bg-green-500/30 transition-colors border border-green-500/30"
          >
            <ChevronLeft className="w-8 h-8 text-white -rotate-90" />
          </button>
        </div>

        {/* 操作说明 */}
        <div className="px-4 py-3 bg-telegram-dark rounded-xl">
          <p className="text-xs text-telegram-secondary text-center">
            💡 提示：点击方向键控制蛇的移动，吃到红色食物得分，不要撞墙或咬到自己！
          </p>
        </div>
      </div>
    </div>
  );
}
